import type { AnnualFridgeModel, CollectionItem, GrowthBoardDay, GrowthSummary } from './models';

export const BOARD_LENGTH = 30;

export function completedBoardDays(days: GrowthBoardDay[]): number {
  return days.filter((day) => day.status === 'completed').length;
}

export function boardProgress(days: GrowthBoardDay[]): number {
  return Math.round((completedBoardDays(days) / Math.max(days.length, 1)) * 100);
}

export function nextBoardDay(days: GrowthBoardDay[]): GrowthBoardDay | undefined {
  return days.find((day) => day.status === 'available') ?? days.find((day) => day.status === 'locked');
}

export function completeBoardDay(days: GrowthBoardDay[], dayId: string, completedAt = new Date().toISOString()): GrowthBoardDay[] {
  const target = days.find((day) => day.id === dayId);
  if (!target || target.status === 'completed') return days;
  const ordered = [...days].sort((a, b) => a.day - b.day);
  const active = ordered.find((day) => day.status === 'available');
  if (active && active.id !== dayId) return days;
  const nextDayNumber = target.day + 1;
  return days.map((day) => {
    if (day.id === dayId) return { ...day, status: 'completed', completedAt };
    if (day.day === nextDayNumber && day.status === 'locked') return { ...day, status: 'available' };
    return day;
  });
}

export function updateGrowthSummary(summary: GrowthSummary, completedAt = new Date().toISOString()): GrowthSummary {
  const currentStreak = summary.currentStreak + 1;
  return {
    ...summary,
    currentStreak,
    longestStreak: Math.max(summary.longestStreak, currentStreak),
    completedBoardDays: summary.completedBoardDays + 1,
    lastActivityAt: completedAt
  };
}

export function fridgeFillRate(fridge: AnnualFridgeModel): number {
  return Math.round((fridge.unlockedSlots / Math.max(fridge.totalSlots, 1)) * 100);
}

export function unlockedCollectionItems(fridge: AnnualFridgeModel, items: CollectionItem[]): CollectionItem[] {
  const ids = new Set(fridge.collectionItemIds);
  return items.filter((item) => ids.has(item.id));
}

export function suggestedFridgeTier(summary: GrowthSummary): AnnualFridgeModel['tier'] {
  const completed = summary.completedBoardDays;
  if (completed >= 120) return 5;
  if (completed >= 90) return 4;
  if (completed >= 60) return 3;
  if (completed >= 30) return 2;
  return 1;
}
